/**
 * src/GamingPage.js
 *
 * VISINVOKE Gaming landing page and premium product gateway.
 *
 * Reached from the top navigation Gaming category panel. It presents the
 * approved product hierarchy in the established VISINVOKE design language:
 *
 *   1. Virtual Shop spans the full width.
 *   2. Veridian and CyberTech have separate cards and dedicated routes.
 *   3. D&D & RPG Generators remains the full-width library below them.
 *
 * The generator library is presented as three experiences: Standard D&D,
 * Quirky Chaotic & Funny, and Tableplay Chaos & Consequences. Each opens the
 * catalogue filtered to that group, with membership and counts from the server.
 *
 * These are deliberate, substantial cards that use the page width, not small
 * utility cards, and not a generic ecommerce grid.
 */
import React, { useEffect } from 'react';
import api from './api';
import { recordProductOpened } from './productActivity';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { glassSurface } from './config/glassTheme';
import { groupIdentity, groupRoute, serverGroups } from './config/generatorExperienceGroups';
import GamingPremiumCards from './GamingPremiumCards';

const GAMING_MOTION_CSS = `
  .gm-reveal {
    opacity: 1;
    transform: translateY(0);
    animation: gm-reveal-in 460ms cubic-bezier(0.22,1,0.36,1) both;
    transition: opacity 460ms cubic-bezier(0.22,1,0.36,1),
                transform 460ms cubic-bezier(0.22,1,0.36,1);
  }
  .gm-reveal.gm-visible { opacity: 1; transform: translateY(0); }
  .gm-reveal-delay-1 { transition-delay: 70ms; animation-delay: 70ms; }
  .gm-reveal-delay-2 { transition-delay: 150ms; animation-delay: 150ms; }
  .gm-reveal-delay-3 { transition-delay: 230ms; animation-delay: 230ms; }
  @keyframes gm-reveal-in {
    from { opacity: 0; transform: translateY(28px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .gm-reveal { animation: none; transition: none; }
  }

  .gm-card {
    transition: transform 240ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 240ms ease, border-color 240ms ease !important;
  }

  @media (max-width: 900px) {
    /* The generator feature head is title-left / figures-right on desktop; it stacks with the rest. */
    .gm-feature-head { flex-direction: column !important; align-items: flex-start !important; gap: 18px !important; }
    .gm-feature-figures { align-self: stretch !important; }
    .gm-gen-head { flex-direction: column !important; align-items: flex-start !important; gap: 18px !important; }
    .gm-gen-head-side { align-self: stretch !important; flex-wrap: wrap !important; }
  }
  @media (max-width: 640px) {
    .gm-page-container { width: 100% !important; max-width: 100vw !important; padding-left: 16px !important; padding-right: 16px !important; }
  }

  /* Three generator experiences: three strong cards side by side on desktop, stacking below 1080px so
     no card is ever squeezed into an unreadable column. */
  .gm-gen-groups { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; align-items: stretch; }
  @media (max-width: 1080px) { .gm-gen-groups { grid-template-columns: 1fr; gap: 20px; } }
  .gm-gen-open:focus-visible, .gm-gen-all:focus-visible { outline: 2px solid #f59e0b; outline-offset: 3px; }
  .gm-gen-art-slice { flex: 1 1 0; min-width: 0; background-size: cover; background-position: center; }
  @media (max-width: 640px) {
    .gm-gen-title-row { flex-direction: column !important; align-items: flex-start !important; gap: 14px !important; }
    .gm-gen-head-side .gm-feature-figures { flex-wrap: wrap !important; gap: 12px 20px !important; padding: 12px 16px !important; }
    .gm-gen-head-side .gm-gen-rule { display: none !important; }
    .gm-gen-all { width: 100% !important; }
  }
`;

/**
 * The third Gaming destination: the standalone D&D / RPG generator library.
 *
 * It spans the full width of the RPG Worlds + Online Gaming row beneath it, so it reads as a
 * destination of the same standing rather than a banner.
 *
 * The count, the starting price and the capability groups are the SERVER's, read from the same
 * commerce catalogue that bills them, never literals here. A figure typed into this file would
 * quietly go stale the moment the catalogue changed, which is exactly how a surface ends up
 * advertising a model that no longer exists. If the catalogue cannot be read, the figures are
 * omitted rather than guessed: unavailable is not zero.
 *
 * These are STANDALONE generators. Nothing here belongs to, or may be described as, Combat PRO.
 */
const GENERATORS_ROUTE = '/dashboard/standalone-generators';
const GEN_ACCENT = '#22C58B';
const GEN_ACCENT_2 = '#0E7A50';
const GEN_BRIGHT = '#4BE0A8';

function usd(cents) {
  return `US$${(cents / 100).toFixed(2)}`;
}

/**
 * Read the commerce catalogue once for the whole generator section. Defensive: this is presentation on
 * a landing page. If the catalogue cannot be read (offline, an error, or an API layer that simply is not
 * there) the section still renders, minus the figures it cannot honestly state. Unavailable is not zero.
 */
function useGeneratorCatalogue() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    let request;
    try { request = api.get('/v1/combat-commerce/catalogue'); } catch (err) { request = null; }
    if (!request || typeof request.then !== 'function') return undefined;
    request
      .then(res => {
        if (!live) return;
        const payload = res && res.data && res.data.standalone_generators;
        if (!payload || !Array.isArray(payload.items)) return;
        // Legacy capability groups: the catalogue's own categories, with the real number in each.
        const counts = new Map();
        payload.items.forEach(item => {
          if (item.available === false) return;   // pre-launch products are not counted as breadth
          const key = item.category || '';
          if (key) counts.set(key, (counts.get(key) || 0) + 1);
        });
        const artBySlug = new Map(payload.items.map(item => [item.slug, item.artwork_path || `/generator-art/${item.slug}.png`]));
        const nameToSlug = new Map(payload.items.map(item => [item.name, item.slug]));
        setData({
          count: payload.count,
          fromCents: payload.individual_price_cents,
          groups: [...counts.entries()].sort((a, b) => b[1] - a[1]),
          experiences: serverGroups(payload).map(group => ({
            ...group,
            art: (group.examples || []).map(name => artBySlug.get(nameToSlug.get(name))).filter(Boolean),
          })),
        });
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  return data;
}

/**
 * Cards that mount after the catalogue arrives miss the page's one-time reveal observer, so they reveal
 * themselves on mount instead (same animation, never left invisible).
 */
function useRevealOnMount() {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame(() => el.classList.add('gm-visible'))
      : null;
    if (raf === null) el.classList.add('gm-visible');
    return () => { if (raf !== null && window.cancelAnimationFrame) window.cancelAnimationFrame(raf); };
  }, []);
  return ref;
}

/**
 * One of the three generator experiences. Its identity (colour, artwork strip, badge) differs by group
 * so the three never read as copies with different labels; its name, positioning line, description,
 * count and examples are the server's.
 */
function GeneratorGroupCard({ group, delay }) {
  const navigate = useNavigate();
  const id = groupIdentity(group.key);
  const chips = group.key === 'standard'
    ? (group.categories || []).slice(0, 3).map(row => ({ label: row.category, count: row.count }))
    : (group.examples || []).slice(0, 3).map(name => ({ label: name }));
  const label = group.short_name || group.name;
  const revealRef = useRevealOnMount();
  return (
    <article
      ref={revealRef}
      className={`gm-card gm-reveal ${delay} gm-gen-group`}
      data-generator-group={group.key}
      style={g.groupCard}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = `0 26px 64px rgba(0,0,0,0.5), 0 0 0 1px ${id.accent}66, 0 0 38px ${id.glow}`; e.currentTarget.style.borderColor = `${id.accent}77`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.38)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}
    >
      <div style={{ ...g.groupArt, background: id.base }} aria-hidden="true">
        {group.art && group.art.length > 0 && (
          <div style={g.groupArtSlices}>
            {group.art.map(url => <div key={url} className="gm-gen-art-slice" style={{ backgroundImage: `url(${url})` }} />)}
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: id.tint }} />
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 40%, ${id.glow} 0%, transparent 72%)` }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${id.accent}, transparent)` }} />
      </div>
      <div style={{ ...g.groupBadge, borderColor: `${id.accent}88`, boxShadow: `0 10px 28px rgba(0,0,0,0.5), 0 0 22px ${id.glow}` }} aria-hidden="true">{id.badge}</div>

      <div style={g.groupBody}>
        <div style={{ ...g.eyebrow, color: id.accent, marginBottom: 10 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: id.accent }} />
          {id.mood}
        </div>
        <h3 style={g.groupTitle}>{group.name}</h3>
        <p style={{ ...g.groupTagline, color: id.bright }}>{group.tagline}</p>
        <p style={g.groupDesc}>{group.description}</p>

        <div style={g.groupFigureRow}>
          <span style={{ ...g.groupCount, color: id.accent }} data-group-count={group.key}>{group.count}</span>
          <span style={g.figureLabel}>{group.count === 1 ? 'Generator' : 'Generators'}</span>
          {group.coming_soon_count > 0 && (
            <span style={{ ...g.groupSoon, borderColor: `${id.accent}55` }}>{group.coming_soon_count} coming soon</span>
          )}
        </div>

        {chips.length > 0 && (
          <div style={g.groupChips}>
            {chips.map(chip => (
              <span key={chip.label} style={{ ...g.featureChip, ...glassSurface(id.accent2, id.accent, id.bright) }}>
                {chip.label}
                {chip.count != null && <span style={{ ...g.featureChipCount, background: id.accent }}>{chip.count}</span>}
              </span>
            ))}
          </div>
        )}

        <button
          type="button"
          className="gm-gen-open"
          style={{ ...g.btn, ...glassSurface(id.accent2, id.accent, id.bright) }}
          aria-label={`Open ${group.name}`}
          onClick={() => navigate(groupRoute(GENERATORS_ROUTE, group.key))}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          Open {label} <span aria-hidden="true" style={{ marginLeft: 8 }}>{'\u2192'}</span>
        </button>
      </div>
    </article>
  );
}

/**
 * The third Gaming destination: the D&D & RPG generator library, presented as three experiences.
 *
 * The overall total and the starting price stay visible in the section head, with a route to the
 * complete collection. Should the server not yet publish experience groups (an older API revision), the
 * established single-panel presentation renders instead of inventing groups here.
 *
 * These are STANDALONE generators. Nothing here belongs to, or may be described as, Combat PRO.
 */
function GeneratorsSection() {
  const navigate = useNavigate();
  const data = useGeneratorCatalogue();
  const experiences = data ? data.experiences : [];
  if (data && !experiences.length) return <LegacyGeneratorsFeature data={data} />;
  return (
    <section className="gm-gen-section" aria-labelledby="gm-gen-title" style={g.genSection}>
      <div className="gm-card gm-reveal gm-reveal-delay-3" style={g.genHead}>
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${GEN_ACCENT}, transparent)` }} />
        <div style={g.featureGlow} />
        <div className="gm-gen-head" style={g.genHeadBody}>
          <div className="gm-gen-title-row" style={{ display: 'flex', alignItems: 'center', gap: 20, minWidth: 0 }}>
            <div style={g.featureBadge} aria-hidden="true">{'\u{1F9ED}'}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...g.eyebrow, color: GEN_ACCENT, marginBottom: 10 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: GEN_ACCENT }} />
                Generator Library
              </div>
              <h2 id="gm-gen-title" style={g.featureTitle}>D&amp;D &amp; RPG Generators</h2>
              <p style={g.genHeadDesc}>
                Three ways into one standalone library for Dungeon Masters and players. Subscribe individually
                or in packs and run each generator on its own, alongside any system you already play.
              </p>
            </div>
          </div>

          <div className="gm-gen-head-side" style={g.genHeadSide}>
            {data && (
              <div className="gm-feature-figures" style={g.featureFigures}>
                <div style={g.figure}>
                  <span style={{ ...g.figureValue, color: GEN_ACCENT }} data-generator-total>{data.count}</span>
                  <span style={g.figureLabel}>Generators</span>
                </div>
                <div className="gm-gen-rule" style={g.figureRule} />
                <div style={g.figure}>
                  <span style={{ ...g.figureValue, color: GEN_ACCENT }}>{usd(data.fromCents)}</span>
                  <span style={g.figureLabel}>From, per month</span>
                </div>
              </div>
            )}
            <button
              type="button"
              className="gm-gen-all"
              style={{ ...g.btn, ...g.genAllBtn, ...glassSurface(GEN_ACCENT_2, GEN_ACCENT, GEN_BRIGHT) }}
              onClick={() => navigate(GENERATORS_ROUTE)}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
            >
              Browse All Generators <span aria-hidden="true" style={{ marginLeft: 8 }}>{'\u2192'}</span>
            </button>
          </div>
        </div>
      </div>

      {experiences.length > 0 && (
        <div className="gm-gen-groups" style={{ marginTop: 24 }}>
          {experiences.map((group, index) => (
            <GeneratorGroupCard key={group.key} group={group} delay={`gm-reveal-delay-${Math.min(index + 1, 3)}`} />
          ))}
        </div>
      )}
    </section>
  );
}

function LegacyGeneratorsFeature({ data }) {
  const navigate = useNavigate();
  const revealRef = useRevealOnMount();

  return (
    <div
      ref={revealRef}
      className="gm-card gm-reveal gm-reveal-delay-3"
      style={g.feature}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = `0 26px 64px rgba(0,0,0,0.5), 0 0 0 1px ${GEN_ACCENT}55`; e.currentTarget.style.borderColor = `${GEN_ACCENT}66`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.38)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${GEN_ACCENT}, transparent)` }} />
      <div style={g.featureGlow} />

      <div style={g.featureBody}>
        <div className="gm-feature-head" style={g.featureHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, minWidth: 0 }}>
            <div style={g.featureBadge} aria-hidden="true">{'\u{1F9ED}'}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...g.eyebrow, color: GEN_ACCENT, marginBottom: 10 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: GEN_ACCENT }} />
                Generator Library
              </div>
              <h2 style={g.featureTitle}>D&amp;D &amp; RPG Generators</h2>
            </div>
          </div>

          {data && (
            <div className="gm-feature-figures" style={g.featureFigures}>
              <div style={g.figure}>
                <span style={{ ...g.figureValue, color: GEN_ACCENT }}>{data.count}</span>
                <span style={g.figureLabel}>Generators</span>
              </div>
              <div style={g.figureRule} />
              <div style={g.figure}>
                <span style={{ ...g.figureValue, color: GEN_ACCENT }}>{usd(data.fromCents)}</span>
                <span style={g.figureLabel}>From, per month</span>
              </div>
            </div>
          )}
        </div>

        <p style={g.featureDesc}>
          The VISINVOKE standalone generator library for Dungeon Masters, with {data ? data.count : 'a growing set of'} focused
          tools you subscribe to individually or in packs, each one built to answer a single question at the table and
          answer it fast. Run them on their own, alongside any system you already play.
        </p>

        {data && (
          <div style={g.featureChips}>
            {data.groups.map(([label, n]) => (
              <span key={label} style={{ ...g.featureChip, ...glassSurface(GEN_ACCENT_2, GEN_ACCENT, GEN_BRIGHT) }}>
                {label}
                <span style={g.featureChipCount}>{n}</span>
              </span>
            ))}
          </div>
        )}

        <div style={g.featureCta}>
          <button
            type="button"
            style={{ ...g.btn, ...g.featureBtn, ...glassSurface(GEN_ACCENT_2, GEN_ACCENT, GEN_BRIGHT) }}
            onClick={() => navigate(GENERATORS_ROUTE)}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Open Generators <span aria-hidden="true" style={{ marginLeft: 8 }}>{'→'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GamingPage() {
  // Universal product activity. This page IS the launch surface for line_gaming, so mounting it
  // is the only honest evidence that the customer opened the product. Once per browser
  // session; the payload carries no identity, no timestamp and no customer content, and a
  // failure is swallowed rather than allowed to break the product.
  useEffect(() => { recordProductOpened('line_gaming', 'web'); }, []);

  useEffect(() => {
    let style = document.getElementById('gaming-motion-css');
    if (!style) { style = document.createElement('style'); style.id = 'gaming-motion-css'; document.head.appendChild(style); }
    style.textContent = GAMING_MOTION_CSS;

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('gm-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.gm-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div style={g.page}>
      <ProductNav />

      <section style={g.headerSection}>
        <div className="vi-desktop-wide gm-page-container" style={g.container}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h1 style={g.headerTitle}>VISINVOKE Gaming</h1>
          </div>
          <p style={g.headerSub}>Build a permanent virtual collection, enter a persistent RPG world, or bring focused D&amp;D and RPG generators to your own table.</p>
        </div>
      </section>

      <section style={g.cardsSection}>
        <div className="vi-desktop-wide gm-page-container" style={g.container}>
          <GamingPremiumCards />

          {/* The established D&D generator library remains below the three premium product cards. */}
          <div style={g.featureRow}>
            <GeneratorsSection />
          </div>
        </div>
      </section>
    </div>
  );
}

const g = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(229,59,99,0.09) 0%, rgba(0,0,0,0) 44%), radial-gradient(1000px 720px at 100% 112%, rgba(124,92,255,0.06) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#C7CFDC',
    overflowX: 'hidden',
  },
  container: { width: '100%', maxWidth: 1280, margin: '0 auto', padding: '0 40px', boxSizing: 'border-box' },

  headerSection: { padding: '48px 0 12px' },
  headerTitle: { fontSize: 52, fontWeight: 900, color: '#ffffff', lineHeight: 1.1, margin: 0, letterSpacing: '-1px', textAlign: 'center' },
  headerSub: { fontSize: 18, fontWeight: 500, color: '#C7CFDC', lineHeight: 1.55, margin: '14px auto 0', textAlign: 'center', maxWidth: 760 },

  cardsSection: { padding: '30px 0 96px' },
  /* The generator feature sits in the same container as the premium cards above. */
  featureRow: { marginTop: 30 },
  feature: {
    position: 'relative',
    background: '#0F1520',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(0,0,0,0.38)',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  featureGlow: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 240, pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 22% 0%, rgba(34,197,139,0.16) 0%, transparent 62%)',
  },
  featureBody: { position: 'relative', padding: '34px 40px 36px', display: 'flex', flexDirection: 'column' },
  featureHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 30, marginBottom: 20 },
  featureBadge: {
    flex: '0 0 auto', width: 72, height: 72, borderRadius: 18, fontSize: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #0B2A20 0%, #0E7A50 58%, #12351f 100%)',
    border: '1px solid rgba(34,197,139,0.34)',
    boxShadow: '0 10px 28px rgba(0,0,0,0.42)',
  },
  featureTitle: {
    fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 900,
    color: '#ffffff', margin: 0, lineHeight: 1.05,
  },
  featureFigures: {
    flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 26,
    background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 16, padding: '14px 26px',
  },
  figure: { display: 'flex', flexDirection: 'column', gap: 3, whiteSpace: 'nowrap' },
  figureValue: { fontSize: 25, fontWeight: 900, lineHeight: 1 },
  figureLabel: { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B97A8' },
  figureRule: { width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.10)' },
  featureDesc: { fontSize: 15.5, color: '#C7CFDC', lineHeight: 1.65, margin: '0 0 22px', maxWidth: 940 },
  featureChips: { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 30 },
  featureChip: {
    display: 'inline-flex', alignItems: 'center', gap: 9,
    fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em', color: '#DCE4F0',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 100, padding: '7px 8px 7px 15px',
  },
  featureChipCount: {
    minWidth: 22, height: 22, borderRadius: 100, padding: '0 6px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11.5, fontWeight: 800, color: '#04120C',
    background: 'rgba(34,197,139,0.85)',
  },
  featureCta: { display: 'flex', justifyContent: 'center' },

  /* Three-experience generator section. */
  genSection: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  genHead: {
    position: 'relative', background: '#0F1520', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.38)', minWidth: 0,
  },
  genHeadBody: {
    position: 'relative', padding: '30px 36px 30px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 30,
  },
  genHeadDesc: { fontSize: 15, color: '#C7CFDC', lineHeight: 1.6, margin: '12px 0 0', maxWidth: 640 },
  genHeadSide: { flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 14 },
  genAllBtn: { marginTop: 0, width: 'auto', padding: '14px 26px', fontSize: 14.5 },
  groupCard: {
    position: 'relative', background: '#0F1520', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20,
    overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.38)', display: 'flex',
    flexDirection: 'column', minWidth: 0,
  },
  groupArt: { position: 'relative', height: 150, overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start' },
  groupArtSlices: { position: 'absolute', inset: 0, display: 'flex', gap: 2, opacity: 0.9 },
  groupBadge: {
    position: 'absolute', zIndex: 2, top: 120, left: 26, width: 60, height: 60, borderRadius: 16,
    fontSize: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(10,13,20,0.92)', border: '1px solid', backdropFilter: 'blur(6px)',
  },
  groupBody: { padding: '40px 28px 28px', display: 'flex', flexDirection: 'column', flex: 1 },
  groupTitle: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 27, fontWeight: 900, color: '#ffffff', margin: '0 0 10px', lineHeight: 1.12 },
  groupTagline: { fontSize: 15.5, fontWeight: 700, fontStyle: 'italic', lineHeight: 1.45, margin: '0 0 12px' },
  groupDesc: { fontSize: 14.5, color: '#C7CFDC', lineHeight: 1.62, margin: '0 0 18px' },
  groupFigureRow: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  groupCount: { fontSize: 34, fontWeight: 900, lineHeight: 1 },
  groupSoon: {
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#DCE4F0',
    border: '1px solid', borderRadius: 100, padding: '4px 10px',
  },
  groupChips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 },
  featureBtn: { marginTop: 0, width: 'auto', minWidth: 300, padding: '17px 46px', fontSize: 16 },

  card: {
    background: '#0F1520',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(0,0,0,0.38)',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  art: {
    position: 'relative',
    height: 176,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  body: { padding: '30px 32px 32px', display: 'flex', flexDirection: 'column', flex: 1 },
  eyebrow: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 14 },
  title: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 900, color: '#ffffff', margin: '0 0 14px', lineHeight: 1.05 },
  desc: { fontSize: 15.5, color: '#C7CFDC', lineHeight: 1.65, margin: '0 0 22px' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28 },
  chip: {
    fontSize: 12.5, fontWeight: 600, color: '#DCE4F0',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 100, padding: '7px 14px',
  },
  btn: {
    marginTop: 'auto',
    width: '100%',
    border: 'none',
    borderRadius: 12,
    padding: '16px 22px',
    fontSize: 15.5,
    fontWeight: 800,
    color: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
    transition: 'filter 180ms ease, transform 160ms cubic-bezier(0.22,1,0.36,1)',
  },
};
